/**
 * Billiards (8-Ball Pool) Game Types
 *
 * All TypeScript types and interfaces for the Billiards game.
 * Table: 800x400 (2:1 ratio) with 6 pockets.
 * Physics-based gameplay with cue stick aiming.
 */

// ============================================================================
// Player & Game Configuration
// ============================================================================

export type BilliardsPlayer = 'player1' | 'player2';
export type BilliardsDifficulty = 'easy' | 'medium' | 'hard';
export type BilliardsPlayerType = 'human' | 'ai';
export type BallType = 'solid' | 'stripe' | '8ball' | 'cue';
export type PlayerAssignment = 'solids' | 'stripes' | 'unassigned';

export type SpinType = 'none' | 'top' | 'back' | 'left' | 'right';

// ============================================================================
// Physics Types
// ============================================================================

export interface Vector2D {
  x: number;
  y: number;
}

export interface BilliardsBall {
  id: string;
  number: number;
  type: BallType;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  mass: number;
  pocketed: boolean;
  pocketedBy?: BilliardsPlayer;
}

export interface BilliardsPhysicsConfig {
  tableWidth: number;
  tableHeight: number;
  cushionWidth: number;
  pocketRadius: number;
  ballRadius: number;
  cueBallRadius: number;
  friction: number;
  rollingFriction: number;
  cushionRestitution: number;
  ballRestitution: number;
  stopThreshold: number;
  maxSpeed: number;
  pocketPositions: Vector2D[];
}

// ============================================================================
// Shot Types
// ============================================================================

export interface Shot {
  angle: number;
  power: number;
  spin: SpinType;
}

export type FoulType =
  | 'scratch'
  | 'no_rail_contact'
  | 'wrong_ball_first'
  | 'early_8ball'
  | 'no_ball_hit';

export interface ShotResult {
  pocketedBalls: BilliardsBall[];
  cueBallPocketed: boolean;
  foul: boolean;
  foulType?: FoulType;
  foulReason?: string;
  continueTurn: boolean;
  gameOver: boolean;
  winner?: BilliardsPlayer;
  assignmentMade: boolean;
}

// ============================================================================
// Game State
// ============================================================================

export type BilliardsGameStatus =
  | { type: 'active' }
  | { type: 'win'; winner: BilliardsPlayer; reason: string }
  | { type: 'loss'; loser: BilliardsPlayer; reason: string };

export interface BilliardsGameState {
  gameId: string;
  balls: BilliardsBall[];
  currentTurn: BilliardsPlayer;
  player1Assignment: PlayerAssignment;
  player2Assignment: PlayerAssignment;
  player1PocketedBalls: BilliardsBall[];
  player2PocketedBalls: BilliardsBall[];
  isBreakShot: boolean;
  tableOpen: boolean;
  gameStatus: BilliardsGameStatus;
  playerTypes: Record<BilliardsPlayer, BilliardsPlayerType>;
  moveHistory: MoveRecord[];
  startedAt: number;
  lastMoveAt: number;
  ballInHand: boolean;
  firstBallHit?: BallType;
  railContactAfterHit: boolean;
}

export interface MoveRecord {
  moveNumber: number;
  player: BilliardsPlayer;
  shot: Shot;
  result: ShotResult;
  timestamp: number;
}

// ============================================================================
// Game Result & Statistics
// ============================================================================

export interface BilliardsGameResult {
  gameId: string;
  mode: BilliardsGameSettings['mode'];
  difficulty?: BilliardsDifficulty;
  winner?: BilliardsPlayer;
  reason: string;
  totalShots: number;
  duration: number;
  completedAt: number;
}

export interface BilliardsGameStats {
  wins: number;
  losses: number;
  winPercentage: number;
  totalGames: number;
  averageBallsPocketed: number;
}

export interface BilliardsStatistics {
  userId?: string;
  singlePlayer: {
    easy: BilliardsGameStats;
    medium: BilliardsGameStats;
    hard: BilliardsGameStats;
  };
  localMultiplayer: BilliardsGameStats;
  onlineMultiplayer: BilliardsGameStats;
  totalGames: number;
  totalPlayTime: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface BilliardsGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: BilliardsDifficulty;
}

// ============================================================================
// Component Props
// ============================================================================

export interface BilliardsGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

// ============================================================================
// Aiming State
// ============================================================================

export interface AimState {
  isAiming: boolean;
  angle: number;
  power: number;
  spin: SpinType;
}

// ============================================================================
// Board Constants
// ============================================================================

export const TABLE_WIDTH = 800;
export const TABLE_HEIGHT = 400;
export const CUSHION_WIDTH = 20;
export const BALL_RADIUS = 10;
export const CUE_BALL_RADIUS = 10;
export const POCKET_RADIUS = 18;
export const MAX_POWER = 1;
export const MAX_SPEED = 2000;

export const POCKET_POSITIONS: Vector2D[] = [
  // Top-left
  { x: CUSHION_WIDTH, y: CUSHION_WIDTH },
  // Top-center
  { x: TABLE_WIDTH / 2, y: CUSHION_WIDTH },
  // Top-right
  { x: TABLE_WIDTH - CUSHION_WIDTH, y: CUSHION_WIDTH },
  // Bottom-left
  { x: CUSHION_WIDTH, y: TABLE_HEIGHT - CUSHION_WIDTH },
  // Bottom-center
  { x: TABLE_WIDTH / 2, y: TABLE_HEIGHT - CUSHION_WIDTH },
  // Bottom-right
  { x: TABLE_WIDTH - CUSHION_WIDTH, y: TABLE_HEIGHT - CUSHION_WIDTH },
];

export const DEFAULT_BILLIARDS_PHYSICS_CONFIG: BilliardsPhysicsConfig = {
  tableWidth: TABLE_WIDTH,
  tableHeight: TABLE_HEIGHT,
  cushionWidth: CUSHION_WIDTH,
  pocketRadius: POCKET_RADIUS,
  ballRadius: BALL_RADIUS,
  cueBallRadius: CUE_BALL_RADIUS,
  friction: 0.985,
  rollingFriction: 0.992,
  cushionRestitution: 0.75,
  ballRestitution: 0.92,
  stopThreshold: 0.3,
  maxSpeed: MAX_SPEED,
  pocketPositions: POCKET_POSITIONS,
};

// Ball colors for rendering
export const BALL_COLORS: Record<number, string> = {
  0: '#FFFFFF',  // Cue ball
  1: '#FFD700',  // Solid yellow
  2: '#0000FF',  // Solid blue
  3: '#FF0000',  // Solid red
  4: '#800080',  // Solid purple
  5: '#FF8C00',  // Solid orange
  6: '#008000',  // Solid green
  7: '#800000',  // Solid maroon
  8: '#000000',  // 8-ball
  9: '#FFD700',  // Stripe yellow
  10: '#0000FF', // Stripe blue
  11: '#FF0000', // Stripe red
  12: '#800080', // Stripe purple
  13: '#FF8C00', // Stripe orange
  14: '#008000', // Stripe green
  15: '#800000', // Stripe maroon
};

export function getBallType(ballNumber: number): BallType {
  if (ballNumber === 0) return 'cue';
  if (ballNumber === 8) return '8ball';
  if (ballNumber >= 1 && ballNumber <= 7) return 'solid';
  return 'stripe';
}
